import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { usePermission } from "../../hooks/usePermission";
import { apiCall } from "../../_services/apiCall";
import config from '../../config/config';
import { getLatestFY } from "../DataManagment/Quantative/component/answerHistoryPage/components/utils";
import { PrimaryButton, SecondaryButton, DangerButton } from "../../globalComponents/ui/Button";
import DataTable from "../../globalComponents/ui/DataTable/DataTable"; // adjust path if needed
import {
  SettingsPage,
  SettingsTopBar, SettingsTopBarLeft, SettingsTopBarRight,
  SettingsAccentBar, SettingsTopBarTitle, SettingsTopBarSub,
  SettingsContent,
  SettingsCard, SettingsSectionHeader, SettingsSectionTitle, SettingsCountChip,
  SettingsTable, SettingsTh, SettingsTr, SettingsTd, SettingsActionsCell, SettingsActionBtn,
  SettingsFilterCard,
} from "./SettingShell";

// ─── Constants ────────────────────────────────────────────────────────────────
const today = new Date().toISOString().split("T")[0];

const UNIT_MAPPING = {
  floor_area: [{ id: 37, label: "ft²" }, { id: 38, label: "yd²" }],
  headcount: [{ id: 43, label: "Number of Employee" }],
};
const UNIT_LABEL_MAP = { 37: "ft²", 38: "yd²", 43: "Number of Employee" };

// ─── Pure helpers ─────────────────────────────────────────────────────────────
const fmtUnitLabel = l => !l ? "" : !l.includes("_") ? l : l.split("_").map(w => w[0].toUpperCase() + w.slice(1)).join(" ");
const fmtDate = d => { try { return d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"; } catch { return "—"; } };
const fmtTime = d => { try { return d ? new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"; } catch { return "—"; } };
const userName = u => u ? `${u.first_name || ""} ${u.last_name || ""}`.trim() : null;
const initials = n => n ? n.trim().split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase()).join("") : "?";
const locBadge = loc => loc.unitCode || [loc._city, loc._zipCode].filter(Boolean).join(" ") || "—";
const locTooltip = loc => loc.location || "";

// ─── Micro components ─────────────────────────────────────────────────────────
function Spinner({ size = 14, color = "#fff" }) {
  return (
    <span style={{ display: "inline-block", width: size, height: size, borderRadius: "50%", border: `2px solid ${color}33`, borderTopColor: color, animation: "obs-spin 0.75s linear infinite", flexShrink: 0 }} />
  );
}

function Avatar({ name, color = "#3f88a5", size = 22 }) {
  if (!name) return null;
  return (
    <span title={name} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: size, height: size, borderRadius: "50%", background: `${color}18`, color, border: `1.5px solid ${color}40`, fontSize: 8, fontWeight: 800, flexShrink: 0, cursor: "default" }}>
      {initials(name)}
    </span>
  );
}

function UnitPill({ loc }) {
  const [show, setShow] = useState(false);
  return (
    <span style={{ position: "relative", display: "inline-block" }} onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: "#f0f9ff", color: "#0369a1", border: "1px solid #bae6fd", whiteSpace: "nowrap", cursor: "default" }}>
        {locBadge(loc)}
      </span>
      {show && locTooltip(loc) && (
        <span style={{ position: "absolute", bottom: "calc(100% + 6px)", left: "50%", transform: "translateX(-50%)", background: "#1e293b", color: "#fff", fontSize: 11, padding: "5px 10px", borderRadius: 7, whiteSpace: "nowrap", boxShadow: "0 4px 14px rgba(0,0,0,0.2)", zIndex: 9999, pointerEvents: "none" }}>
          {locTooltip(loc)}
          <span style={{ position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)", borderWidth: 4, borderStyle: "solid", borderColor: "#1e293b transparent transparent transparent" }} />
        </span>
      )}
    </span>
  );
}

const inputSt = (err, dis) => ({ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1.5px solid ${err ? "#fca5a5" : dis ? "#f1f5f9" : "#e2e8f0"}`, fontSize: 13, outline: "none", color: dis ? "#475569" : "#1e293b", background: dis ? "#f8fafc" : "#fff", boxSizing: "border-box", cursor: dis ? "default" : "text" });
const selectSt = (err, dis) => ({ ...inputSt(err, dis), appearance: "none", WebkitAppearance: "none", backgroundImage: dis ? "none" : `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%2394a3b8'/%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center", paddingRight: dis ? 12 : 32, cursor: dis ? "default" : "pointer" });

function Field({ label, required, error, hint, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px" }}>
        {label}{required && <span style={{ color: "#ef4444", marginLeft: 3 }}>*</span>}
      </label>
      {children}
      {error && <span style={{ fontSize: 10, color: "#ef4444", fontWeight: 600 }}>⚠ {error}</span>}
      {!error && hint && <span style={{ fontSize: 10, color: "#94a3b8" }}>{hint}</span>}
    </div>
  );
}

// ─── FY Dropdown ──────────────────────────────────────────────────────────────
function FYDropdown({ fyList, selectedFY, onChange, latestFYId }) {
  const isPast = selectedFY?.id !== latestFYId;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b", whiteSpace: "nowrap" }}>Financial Year</span>
      <div style={{ position: "relative" }}>
        <select
          value={selectedFY?.id ?? ""}
          onChange={e => { const f = fyList.find(f => String(f.id) === e.target.value); if (f) onChange(f); }}
          style={{ padding: "6px 28px 6px 10px", borderRadius: 8, border: `1.5px solid ${isPast ? "#f59e0b" : "#e2e8f0"}`, background: isPast ? "#fffbeb" : "#fff", fontSize: 12, fontWeight: 600, color: isPast ? "#b45309" : "#1e293b", cursor: "pointer", outline: "none", appearance: "none", WebkitAppearance: "none", minWidth: 140 }}
        >
          {fyList.map(fy => (
            <option key={fy.id} value={fy.id}>
              {fy.financial_year_value}{fy.id === latestFYId ? " (Current)" : ""}
            </option>
          ))}
        </select>
        <span style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", fontSize: 9, color: isPast ? "#b45309" : "#94a3b8", pointerEvents: "none" }}>▾</span>
      </div>
    </div>
  );
}

// ─── Status badge renderer ────────────────────────────────────────────────────
function StatusChip({ label, color, bg, border }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 20, background: bg, border: `1px solid ${border}`, fontSize: 11, fontWeight: 700, color }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: color }} />
      {label}
    </span>
  );
}

// ─── Multi-Select Filter Dropdown ─────────────────────────────────────────────
function FilterDropdown({ filters, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const OPTIONS = [
    { key: "active", label: "Active", color: "#16a34a", dot: "#22c55e" },
    { key: "inactive", label: "Historical", color: "#475569", dot: "#94a3b8" },
    { key: "deleted", label: "Deleted", color: "#dc2626", dot: "#ef4444" },
  ];

  useEffect(() => {
    if (!open) return;
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const toggle = key => {
    const next = filters.includes(key) ? filters.filter(f => f !== key) : [...filters, key];
    if (next.length > 0) onChange(next);
  };

  const allSelected = OPTIONS.every(o => filters.includes(o.key));
  const label = allSelected ? "All Records" : OPTIONS.filter(o => filters.includes(o.key)).map(o => o.label).join(", ") || "None";

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 8, border: `1.5px solid ${open ? "#93c5fd" : "#e2e8f0"}`, background: open ? "#f0f9ff" : "#fff", fontSize: 12, fontWeight: 600, color: "#475569", cursor: "pointer", whiteSpace: "nowrap", minWidth: 160 }}
      >
        <span style={{ fontSize: 11 }}>⊞</span>
        <span style={{ flex: 1, textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 160 }}>{label}</span>
        <span style={{ fontSize: 9, color: "#94a3b8" }}>{open ? "▴" : "▾"}</span>
      </button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, background: "#fff", borderRadius: 10, border: "1.5px solid #e2e8f0", boxShadow: "0 8px 28px rgba(0,0,0,0.12)", minWidth: 190, zIndex: 200, overflow: "hidden" }}>
          <div style={{ padding: "6px 4px" }}>
            <button
              onClick={() => onChange(allSelected ? ["active"] : OPTIONS.map(o => o.key))}
              style={{ display: "flex", alignItems: "center", gap: 9, width: "100%", padding: "8px 12px", border: "none", background: "transparent", cursor: "pointer", fontSize: 12, fontWeight: 600, color: "#0f172a", textAlign: "left" }}
              onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            >
              <span style={{ width: 15, height: 15, borderRadius: 4, border: `1.5px solid ${allSelected ? "#3f88a5" : "#cbd5e1"}`, background: allSelected ? "#3f88a5" : "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {allSelected && <span style={{ color: "#fff", fontSize: 9, lineHeight: 1 }}>✓</span>}
              </span>
              All Records
            </button>
            <div style={{ height: 1, background: "#f1f5f9", margin: "4px 0" }} />
            {OPTIONS.map(o => {
              const checked = filters.includes(o.key);
              return (
                <button key={o.key} onClick={() => toggle(o.key)}
                  style={{ display: "flex", alignItems: "center", gap: 9, width: "100%", padding: "8px 12px", border: "none", background: "transparent", cursor: "pointer", fontSize: 12, fontWeight: 600, color: o.color, textAlign: "left" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  <span style={{ width: 15, height: 15, borderRadius: 4, border: `1.5px solid ${checked ? o.color : "#cbd5e1"}`, background: checked ? `${o.color}18` : "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {checked && <span style={{ color: o.color, fontSize: 9, lineHeight: 1 }}>✓</span>}
                  </span>
                  <span style={{ width: 7, height: 7, borderRadius: "50%", background: o.dot, flexShrink: 0 }} />
                  {o.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Delete Confirm ───────────────────────────────────────────────────────────
function DeleteConfirm({ cfg, onConfirm, onCancel }) {
  const [checked, setChecked] = useState(false);
  useEffect(() => {
    const h = e => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onCancel]);

  return (
    <div onClick={onCancel} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", backdropFilter: "blur(3px)", zIndex: 1200, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 420, boxShadow: "0 24px 64px rgba(0,0,0,0.22)", overflow: "hidden" }}>
        <div style={{ padding: "16px 20px 12px", borderBottom: "1px solid #fee2e2", background: "#fef2f2" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#dc2626" }}>Delete Configuration</div>
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
            Effective {fmtDate(cfg.fromDate)}{cfg.toDate ? ` → ${fmtDate(cfg.toDate)}` : ""}
          </div>
        </div>
        <div style={{ margin: "14px 20px 0", padding: "10px 12px", borderRadius: 9, background: "#fffbeb", border: "1px solid #fcd34d", display: "flex", gap: 8 }}>
          <span style={{ fontSize: 14, flexShrink: 0 }}>⚠️</span>
          <div style={{ fontSize: 11, color: "#b45309", lineHeight: 1.6 }}>
            This configuration is used in ESG dashboards, carbon footprint calculations and GHG reports. Deleting it may cause incomplete or incorrect data in those modules.
          </div>
        </div>
        <div style={{ padding: "12px 20px" }}>
          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer" }}>
            <input type="checkbox" checked={checked} onChange={e => setChecked(e.target.checked)} style={{ marginTop: 2, accentColor: "#dc2626", width: 14, height: 14, flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: "#475569", lineHeight: 1.6 }}>I understand this may affect dashboard data and confirm deletion.</span>
          </label>
        </div>
        <div style={{ padding: "8px 20px 16px", display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
          <DangerButton onClick={onConfirm} disabled={!checked}>Delete</DangerButton>
        </div>
      </div>
    </div>
  );
}

// ─── History Drawer ───────────────────────────────────────────────────────────
function HistoryDrawer({ configId, onClose }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { isSuccess, data } = await apiCall(`${config.POSTLOGIN_API_URL_COMPANY}ownershipControl/${configId}/history`, {}, {}, "GET");
      if (isSuccess && Array.isArray(data)) setHistory(data);
      setLoading(false);
    })();
  }, [configId]);

  useEffect(() => {
    const h = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const FL = k => ({ ownershipType: "Ownership", controlType: "Control", allocationMethod: "Allocation", fromDate: "From", toDate: "To", totalValue: "Total", occupiedValue: "Occupied", unitId: "Unit", subLocationId: "Sub-loc", isDisabled: "Status" }[k] || k);
  const FV = (k, v) => {
    if (v === null || v === undefined || v === "") return "—";
    if (k === "isDisabled") return v === true || v === "true" ? "Disabled" : "Active";
    if (k === "unitId") return fmtUnitLabel(UNIT_LABEL_MAP[v] || String(v));
    if (k === "allocationMethod") return v === "floor_area" ? "Floor Area" : v === "headcount" ? "Headcount" : v;
    if (k === "ownershipType" || k === "controlType") return v[0].toUpperCase() + v.slice(1);
    if (k === "fromDate" || k === "toDate") return fmtDate(v);
    if (typeof v === "number") return v.toLocaleString();
    return String(v);
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", backdropFilter: "blur(3px)", zIndex: 1100, display: "flex", justifyContent: "flex-end" }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 460, background: "#fff", height: "100%", overflowY: "auto", boxShadow: "-8px 0 40px rgba(0,0,0,0.16)", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "16px 22px", borderBottom: "1px solid #e2e8f0", background: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>Edit History</div>
            <div style={{ fontSize: 11, color: "#94a3b8" }}>Record #{configId}</div>
          </div>
          <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: 7, border: "1.5px solid #e2e8f0", background: "#fff", cursor: "pointer", fontSize: 16, color: "#94a3b8", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
        </div>
        <div style={{ padding: "18px 22px", flex: 1 }}>
          {loading ? (
            <div style={{ display: "flex", alignItems: "center", gap: 10, color: "#64748b", fontSize: 13 }}>
              <Spinner size={14} color="#3f88a5" /> Loading…
            </div>
          ) : history.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>
              <div style={{ fontSize: 26, marginBottom: 8 }}>📋</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#475569" }}>No edits yet</div>
            </div>
          ) : history.map((entry, i) => {
            const editor = userName(entry.changedUser);
            const isDisableEvent = entry.changedFields?.length === 1 && entry.changedFields[0]?.field === "isDisabled";
            return (
              <div key={entry.id || i} style={{ marginBottom: 12, borderRadius: 10, border: "1.5px solid #e2e8f0", overflow: "hidden" }}>
                <div style={{ padding: "9px 14px", background: isDisableEvent ? "#fef2f2" : "#f8fafc", borderBottom: `1px solid ${isDisableEvent ? "#fecaca" : "#e2e8f0"}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {isDisableEvent
                      ? <span style={{ fontSize: 11, fontWeight: 800, padding: "2px 8px", borderRadius: 20, background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca" }}>🗑 Deleted</span>
                      : <span style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>Edit #{i + 1}</span>}
                    <span style={{ fontSize: 10, color: "#94a3b8" }}>{fmtTime(entry.editedAt)}</span>
                  </div>
                  {editor && (
                    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                      <Avatar name={editor} color={isDisableEvent ? "#dc2626" : "#7c3aed"} size={20} />
                      <span style={{ fontSize: 11, color: "#475569", fontWeight: 600 }}>{editor}</span>
                    </div>
                  )}
                </div>
                <div style={{ padding: "10px 14px", display: "flex", flexDirection: "column", gap: 7 }}>
                  {entry.changedFields?.map(({ field, oldValue, newValue }) => {
                    const isDisableField = field === "isDisabled";
                    return (
                      <div key={field}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: isDisableField ? "#dc2626" : "#94a3b8", textTransform: "uppercase", marginBottom: 3 }}>{FL(field)}</div>
                        {isDisableField
                          ? <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 5, background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca", fontWeight: 700 }}>Record Disabled</span>
                          : (
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 5, background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca", textDecoration: "line-through" }}>{FV(field, oldValue)}</span>
                              <span style={{ fontSize: 10, color: "#94a3b8" }}>→</span>
                              <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 5, background: "#f0fdf4", color: "#16a34a", border: "1px solid #bbf7d0" }}>{FV(field, newValue)}</span>
                            </div>
                          )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Config Modal ─────────────────────────────────────────────────────────────
function ConfigModal({ location, existing, prefill, mode, selectedFY, onSave, onClose }) {
  const isView = mode === "view", isEdit = mode === "edit", isNew = mode === "new";
  const blank = { ownershipType: "", controlType: "operational", allocationMethod: "", fromDate: today, totalValue: "", occupiedValue: "", subLocationId: "", unitId: "" };
  const build = d => ({ ownershipType: d.ownershipType || "", controlType: d.controlType || "operational", allocationMethod: d.allocationMethod || "", fromDate: isNew ? today : d.fromDate || "", unitId: d.unitId || "", totalValue: d.totalValue != null ? String(d.totalValue) : "", occupiedValue: d.occupiedValue != null ? String(d.occupiedValue) : "", subLocationId: d.subLocationId || "" });

  const [form, setForm] = useState(() => isView || isEdit ? (existing ? build(existing) : blank) : (isNew && prefill ? build(prefill) : blank));
  const [errors, setErrs] = useState({});
  const [saving, setSave] = useState(false);

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); if (errors[k]) setErrs(p => { const n = { ...p }; delete n[k]; return n; }); };
  const vf = (k, v) => {
    const e = { ...errors }; delete e[k];
    if (["ownershipType", "allocationMethod", "unitId", "fromDate", "totalValue", "occupiedValue"].includes(k) && (!v || v === "")) e[k] = "Required";
    if (k === "occupiedValue" && form.totalValue && parseFloat(v) > parseFloat(form.totalValue)) e[k] = "Cannot exceed total";
    setErrs(e);
  };
  const validate = () => {
    const e = {};
    ["ownershipType", "allocationMethod", "unitId", "fromDate", "totalValue", "occupiedValue"].forEach(k => { if (!form[k]) e[k] = "Required"; });
    if (form.totalValue && form.occupiedValue && parseFloat(form.occupiedValue) > parseFloat(form.totalValue)) e.occupiedValue = "Cannot exceed total";
    setErrs(e); return Object.keys(e).length === 0;
  };
  const save = async () => {
    if (!validate()) return; setSave(true);
    await onSave({ ...form, financialYearId: selectedFY.id, locationId: location.id, subLocationId: form.subLocationId || null, totalValue: parseFloat(form.totalValue), occupiedValue: parseFloat(form.occupiedValue) });
    setSave(false);
  };

  const pct = form.totalValue && form.occupiedValue ? ((parseFloat(form.occupiedValue) / parseFloat(form.totalValue)) * 100).toFixed(1) : null;
  const unitOpts = UNIT_MAPPING[form.allocationMethod] || [];
  const unitLbl = unitOpts.find(u => String(u.id) === String(form.unitId))?.label;

  useEffect(() => {
    const h = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const headerBg = isEdit ? "#fef9f9" : isView ? "#f0f9ff" : "#f8fafc";

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", backdropFilter: "blur(3px)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 580, boxShadow: "0 32px 80px rgba(0,0,0,0.25)", overflow: "hidden" }}>
        <div style={{ padding: "15px 22px", borderBottom: "1px solid #e2e8f0", background: headerBg, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>{isView ? "View" : isEdit ? "Edit" : "New"} Configuration</span>
              {isView && existing?.isDisabled && (
                <span style={{ fontSize: 9, fontWeight: 800, padding: "2px 8px", borderRadius: 20, background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca", textTransform: "uppercase" }}>Deleted</span>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3 }}>
              <UnitPill loc={location} />
              <span style={{ width: 1, height: 9, background: "#cbd5e1", display: "inline-block" }} />
              <span style={{ fontSize: 11, fontWeight: 600, color: "#3f88a5" }}>{selectedFY?.financial_year_value}</span>
            </div>
          </div>
          <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: 7, border: "1.5px solid #e2e8f0", background: "#fff", cursor: "pointer", fontSize: 16, color: "#94a3b8", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
        </div>
        <div style={{ padding: "18px 22px", maxHeight: "68vh", overflowY: "auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 13 }}>
            {location.subLocation?.length > 0 && (
              <Field label="Sub-location">
                {isView
                  ? <div style={inputSt(false, true)}>{location.subLocation.find(s => String(s.id) === String(form.subLocationId))?.subLocation || "None"}</div>
                  : <select style={selectSt(false)} value={form.subLocationId} onChange={e => set("subLocationId", e.target.value)}>
                    <option value="">None (entire location)</option>
                    {location.subLocation.map(s => <option key={s.id} value={s.id}>{s.subLocation}</option>)}
                  </select>}
              </Field>
            )}
            <Field label="Control Type" required>
              {isView ? <div style={inputSt(false, true)}>{form.controlType || "—"}</div>
                : <select style={selectSt(false)} value={form.controlType} onChange={e => set("controlType", e.target.value)}>
                  <option value="operational">Operational</option>
                </select>}
            </Field>
            <Field label="Ownership Type" required error={errors.ownershipType}>
              {isView ? <div style={inputSt(false, true)}>{form.ownershipType || "—"}</div>
                : <select style={selectSt(!!errors.ownershipType)} value={form.ownershipType} onChange={e => set("ownershipType", e.target.value)} onBlur={e => vf("ownershipType", e.target.value)}>
                  <option value="">Select…</option>
                  <option value="owned">Owned</option>
                  <option value="leased">Leased</option>
                  <option value="rented">Rented</option>
                </select>}
            </Field>
            <Field label="Effective From" required error={errors.fromDate}>
              <input type={isView ? "text" : "date"} readOnly={isView} style={inputSt(!!errors.fromDate, isView)}
                value={isView ? fmtDate(form.fromDate) : form.fromDate}
                onChange={e => !isView && set("fromDate", e.target.value)}
                onBlur={e => !isView && vf("fromDate", e.target.value)} />
            </Field>
            <Field label="Allocation Method" required error={errors.allocationMethod}>
              {isView ? <div style={inputSt(false, true)}>{form.allocationMethod === "floor_area" ? "Floor Area" : form.allocationMethod === "headcount" ? "Headcount" : "—"}</div>
                : <select style={selectSt(!!errors.allocationMethod)} value={form.allocationMethod}
                  onChange={e => { const m = e.target.value, u = UNIT_MAPPING[m] || []; setForm(f => ({ ...f, allocationMethod: m, unitId: u.length ? u[0].id : "" })); if (errors.allocationMethod) setErrs(p => { const n = { ...p }; delete n.allocationMethod; return n; }); }}
                  onBlur={e => vf("allocationMethod", e.target.value)}>
                  <option value="">Select…</option>
                  <option value="floor_area">Floor Area</option>
                  <option value="headcount">Headcount</option>
                </select>}
            </Field>
            <Field label="Unit" required error={errors.unitId}>
              {isView ? <div style={inputSt(false, true)}>{unitLbl ? fmtUnitLabel(unitLbl) : "—"}</div>
                : <select style={selectSt(!!errors.unitId)} value={form.unitId} onChange={e => set("unitId", e.target.value)} onBlur={e => vf("unitId", e.target.value)} disabled={!form.allocationMethod}>
                  <option value="">Select…</option>
                  {unitOpts.map(u => <option key={u.id} value={u.id}>{fmtUnitLabel(u.label)}</option>)}
                </select>}
            </Field>
            <Field label="Total Value" required error={errors.totalValue}>
              <input type={isView ? "text" : "number"} readOnly={isView} min="0" placeholder="e.g. 12000" style={inputSt(!!errors.totalValue, isView)}
                value={isView ? (form.totalValue ? Number(form.totalValue).toLocaleString() : "—") : form.totalValue}
                onChange={e => !isView && set("totalValue", e.target.value)}
                onBlur={e => !isView && vf("totalValue", e.target.value)} />
            </Field>
            <Field label="Occupied Value" required error={errors.occupiedValue}>
              <input type={isView ? "text" : "number"} readOnly={isView} min="0" placeholder="e.g. 8500" style={inputSt(!!errors.occupiedValue, isView)}
                value={isView ? (form.occupiedValue ? Number(form.occupiedValue).toLocaleString() : "—") : form.occupiedValue}
                onChange={e => !isView && set("occupiedValue", e.target.value)}
                onBlur={e => !isView && vf("occupiedValue", e.target.value)} />
            </Field>
          </div>
          {pct !== null && (
            <div style={{ marginTop: 12, padding: "9px 12px", background: "#f0f9ff", borderRadius: 7, border: "1px solid #bae6fd", fontSize: 12, color: "#0369a1", fontWeight: 600 }}>
              📊 {pct}% occupancy
            </div>
          )}
        </div>
        <div style={{ padding: "12px 22px", borderTop: "1px solid #f1f5f9", background: "#f8fafc", display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          {!isView && (
            <PrimaryButton onClick={save} disabled={saving}>
              {saving ? "Saving..." : isEdit ? "Save Changes" : "Add Configuration"}
            </PrimaryButton>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Location Detail ──────────────────────────────────────────────────────────
function LocationDetail({ loc, allConfigs, configLoading, isViewOnly, onSave, onDelete, onBack, selectedFY, financialYears, latestFYId, onFYChange, canCreate, canUpdate, canDelete }) {
  const [modal, setModal] = useState(null);
  const [drawer, setDrawer] = useState(null);
  const [delConfirm, setDelConfirm] = useState(null);
  const [filters, setFilters] = useState(["active", "inactive", "deleted"]);

  const closeModal = useCallback(() => setModal(null), []);

  const active = useMemo(() => (allConfigs || []).filter(c => !c.toDate && !c.isDisabled).sort((a, b) => new Date(b.fromDate) - new Date(a.fromDate)), [allConfigs]);
  const inactive = useMemo(() => (allConfigs || []).filter(c => c.toDate && !c.isDisabled).sort((a, b) => new Date(b.fromDate) - new Date(a.fromDate)), [allConfigs]);
  const deleted = useMemo(() => (allConfigs || []).filter(c => c.isDisabled).sort((a, b) => new Date(b.fromDate) - new Date(a.fromDate)), [allConfigs]);
  const latestActive = active[0] || null;

  const handleSave = async fd => {
    await onSave(loc.id, fd, modal?.mode === "edit" ? "correction" : "update", modal?.cfg);
    closeModal();
  };

  // Build flat rows for DataTable with a "recordType" tag
  const tableRows = useMemo(() => {
    const rows = [];
    if (filters.includes("active")) active.forEach(c => rows.push({ ...c, _id: String(c.id), _recordType: "active" }));
    if (filters.includes("inactive")) inactive.forEach(c => rows.push({ ...c, _id: String(c.id), _recordType: "inactive" }));
    if (filters.includes("deleted")) deleted.forEach(c => rows.push({ ...c, _id: String(c.id), _recordType: "deleted" }));
    return rows;
  }, [active, inactive, deleted, filters]);

  // DataTable column definitions
  const columns = useMemo(() => [
    {
      key: "_recordType",
      label: "Status",
      width: "110px",
      renderCell: (val) => {
        if (val === "active") return <StatusChip label="Active" color="#16a34a" bg="#f0fdf4" border="#bbf7d0" />;
        if (val === "inactive") return <StatusChip label="Historical" color="#475569" bg="#f8fafc" border="#e2e8f0" />;
        if (val === "deleted") return <StatusChip label="Deleted" color="#dc2626" bg="#fef2f2" border="#fecaca" />;
        return null;
      },
    },
    {
      key: "fromDate",
      label: "Effective From",
      width: "130px",
      sortable: true,
      renderCell: val => <span style={{ fontWeight: 600, color: "#0f172a", fontSize: 13 }}>{fmtDate(val)}</span>,
    },
    {
      key: "toDate",
      label: "Effective To",
      width: "120px",
      renderCell: val => <span style={{ color: val ? "#1e293b" : "#94a3b8", fontSize: 13 }}>{val ? fmtDate(val) : "—"}</span>,
    },
    {
      key: "ownershipType",
      label: "Ownership",
      width: "110px",
      sortable: true,
      renderCell: val => val ? <span style={{ fontSize: 13, textTransform: "capitalize" }}>{val}</span> : <span style={{ color: "#94a3b8" }}>—</span>,
    },
    {
      key: "allocationMethod",
      label: "Allocation",
      width: "120px",
      renderCell: val => (
        <span style={{ fontSize: 13 }}>
          {val === "floor_area" ? "Floor Area" : val === "headcount" ? "Headcount" : val || "—"}
        </span>
      ),
    },
    {
      key: "totalValue",
      label: "Total",
      width: "120px",
      renderCell: (val, row) => {
        const unitLbl = fmtUnitLabel(UNIT_LABEL_MAP[row.unitId] || "");
        return (
          <span style={{ fontWeight: 600, fontSize: 13 }}>
            {val?.toLocaleString() ?? "—"}
            {unitLbl && <span style={{ color: "#94a3b8", fontWeight: 400, marginLeft: 3, fontSize: 11 }}>{unitLbl}</span>}
          </span>
        );
      },
    },
    {
      key: "occupiedValue",
      label: "Occupied",
      width: "120px",
      renderCell: (val, row) => {
        const unitLbl = fmtUnitLabel(UNIT_LABEL_MAP[row.unitId] || "");
        return (
          <span style={{ fontSize: 13 }}>
            {val?.toLocaleString() ?? "—"}
            {unitLbl && <span style={{ color: "#94a3b8", fontWeight: 400, marginLeft: 3, fontSize: 11 }}>{unitLbl}</span>}
          </span>
        );
      },
    },
    {
      key: "_pct",
      label: "Usage %",
      width: "90px",
      renderCell: (_, row) => {
        const pct = row.totalValue && row.occupiedValue
          ? ((row.occupiedValue / row.totalValue) * 100).toFixed(1)
          : null;
        if (!pct) return <span style={{ color: "#94a3b8" }}>—</span>;
        const color = parseFloat(pct) > 90 ? "#dc2626" : parseFloat(pct) > 70 ? "#d97706" : "#16a34a";
        return <span style={{ fontWeight: 700, color, fontSize: 13 }}>{pct}%</span>;
      },
    },
  ], []);

  // Extra actions per row
  const extraActions = useCallback((row) => {
    const isDeleted = row._recordType === "deleted";
    const canEdit = !row.toDate && !isDeleted && !isViewOnly && canUpdate;
    const actions = [
      {
        label: "View",
        icon: "fas fa-eye",
        variant: "view",
        onClick: () => setModal({ mode: "view", cfg: row }),
      },
      {
        label: "History",
        icon: "fas fa-clock",
        variant: "view",
        onClick: () => setDrawer({ id: row.id }),
      },
    ];
    if (canEdit) {
      actions.splice(1, 0, {
        label: "Edit",
        icon: "fas fa-pencil-alt",
        variant: "edit",
        onClick: () => setModal({ mode: "edit", cfg: row }),
      });
    }
    if (!isDeleted && !isViewOnly && canDelete) {
      actions.push({
        label: "Delete",
        icon: "fas fa-trash",
        variant: "del",
        onClick: () => setDelConfirm(row),
      });
    }
    return actions;
  }, [isViewOnly, canUpdate, canDelete]);

  return (
    <>
      {modal && <ConfigModal location={loc} existing={modal.cfg || null} prefill={modal.mode === "new" ? latestActive : null} mode={modal.mode} selectedFY={selectedFY} onSave={handleSave} onClose={closeModal} />}
      {drawer && <HistoryDrawer configId={drawer.id} onClose={() => setDrawer(null)} />}
      {delConfirm && <DeleteConfirm cfg={delConfirm} onCancel={() => setDelConfirm(null)} onConfirm={async () => { await onDelete(loc.id, delConfirm.id); setDelConfirm(null); }} />}

      {/* ── Top bar — fixed layout to prevent overlap ─────────────────────── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, padding: "14px 20px", borderBottom: "1px solid #e2e8f0", background: "#fff" }}>
        {/* Left: back + title */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
          <SecondaryButton onClick={onBack}>
            <i className="fas fa-arrow-left" style={{ marginRight: 6 }} /> Back
          </SecondaryButton>
          <div style={{ width: 3, height: 32, borderRadius: 2, background: "#3f88a5", flexShrink: 0 }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {loc.unitCode ? loc.unitCode : loc.location}
            </div>
            <div style={{ fontSize: 11, color: "#94a3b8" }}>Manage ownership &amp; control for this location</div>
          </div>
        </div>

        {/* Right: FY picker + button — flex-wrapped so they never overlap */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0, flexWrap: "wrap" }}>
          {financialYears.length > 0 && selectedFY && (
            <FYDropdown fyList={financialYears} selectedFY={selectedFY} onChange={onFYChange} latestFYId={latestFYId} />
          )}
          {!isViewOnly && canCreate && (
            <PrimaryButton onClick={() => setModal({ mode: "new" })}>
              <i className="fas fa-plus" style={{ marginRight: 6 }} /> New Configuration
            </PrimaryButton>
          )}
        </div>
      </div>

      {/* ── Content ───────────────────────────────────────────────────────── */}
      <SettingsContent $wide>
        {isViewOnly && (
          <SettingsFilterCard style={{ background: "#fffbeb", border: "1px solid #fcd34d", marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 700, color: "#92400e" }}>
              <i className="fas fa-lock" /> View Only — {selectedFY?.financial_year_value}
            </div>
          </SettingsFilterCard>
        )}

        <SettingsCard>
          <SettingsSectionHeader>
            <i className="fas fa-building" style={{ fontSize: 12, color: "#3f88a5" }} />
            <SettingsSectionTitle>Ownership History</SettingsSectionTitle>
            <SettingsCountChip>{tableRows.length}</SettingsCountChip>
            <div style={{ marginLeft: "auto" }}>
              <FilterDropdown filters={filters} onChange={setFilters} />
            </div>
          </SettingsSectionHeader>

          {configLoading ? (
            <div style={{ padding: 60, textAlign: "center" }}><Spinner size={20} color="#3f88a5" /></div>
          ) : (
            <DataTable
              title=""
              columns={columns}
              data={tableRows}
              extraActions={extraActions}
              searchable={true}
              pageSize={10}
              showPagination={true}
              emptyMessage="No configurations found matching current filters."
            />
          )}
        </SettingsCard>
      </SettingsContent>
    </>
  );
}

// ─── Locations List ───────────────────────────────────────────────────────────
function LocationsList({ locations, locLoading, configs, isViewOnly, financialYears, selectedFY, fyLoading, latestFYId, onFYChange, onLocationClick, configuredCount }) {
  const [search, setSearch] = useState("");
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return q ? locations.filter(l => l.location?.toLowerCase().includes(q) || (l.unitCode && l.unitCode.toLowerCase().includes(q))) : locations;
  }, [locations, search]);

  const getC = id => selectedFY ? configs[`${id}_${selectedFY.id}`] : undefined;

  // DataTable columns for locations list
  const columns = useMemo(() => [
    {
      key: "location",
      label: "Location Name",
      sortable: true,
      renderCell: (val, row) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ color: "#0f172a", fontSize: 13 }}>{val}</span>
          {row.subLocation?.length > 0 && (
            <SettingsCountChip>{row.subLocation.length} sub</SettingsCountChip>
          )}
        </div>
      ),
    },
    {
      key: "unitCode",
      label: "Unit",
      width: "130px",
      renderCell: (_, row) => <UnitPill loc={row} />,
    },
    {
      key: "_status",
      label: "Status",
      width: "160px",
      renderCell: (_, row) => {
        const lc = getC(row.id);
        const loading = lc === undefined;
        const active = Array.isArray(lc) ? lc.filter(c => !c.isDisabled).length : 0;
        const hasActive = active > 0;
        return (
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 20, background: loading ? "#f8fafc" : hasActive ? "#f0fdf4" : "#fffbeb", border: `1px solid ${loading ? "#e2e8f0" : hasActive ? "#bbf7d0" : "#fde68a"}` }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: loading ? "#cbd5e1" : hasActive ? "#22c55e" : "#f59e0b" }} />
            <span style={{ fontSize: 11, fontWeight: 700, color: loading ? "#94a3b8" : hasActive ? "#16a34a" : "#d97706" }}>
              {loading ? "Loading…" : hasActive ? `${active} Active` : "Not Configured"}
            </span>
          </div>
        );
      },
    },
  ], [configs, selectedFY]);

  const locationRows = useMemo(() =>
    filtered.map(l => ({ ...l, _id: String(l.id), _status: null })),
    [filtered]);

  return (
    <>
      <SettingsTopBar>
        <SettingsTopBarLeft>
          <SettingsAccentBar />
          <div>
            <SettingsTopBarTitle>Ownership Control</SettingsTopBarTitle>
            <SettingsTopBarSub>Configure reporting ownership for each location</SettingsTopBarSub>
          </div>
        </SettingsTopBarLeft>
        <SettingsTopBarRight>
          {fyLoading
            ? <Spinner size={12} color="#94a3b8" />
            : (financialYears.length > 0 && selectedFY && (
              <FYDropdown fyList={financialYears} selectedFY={selectedFY} onChange={onFYChange} latestFYId={latestFYId} />
            ))}
        </SettingsTopBarRight>
      </SettingsTopBar>

      <SettingsContent $wide>
        {/* Summary cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 24 }}>
          <SettingsCard style={{ padding: 16, display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: "#f0f9ff", display: "flex", alignItems: "center", justifyContent: "center", color: "#3f88a5", fontSize: 20 }}>
              <i className="fas fa-map-marker-alt" />
            </div>
            <div>
              <div style={{ fontSize: 20, fontWeight: 800, color: "#0f172a" }}>{locations.length}</div>
              <div style={{ fontSize: 11, color: "#64748b" }}>Total Locations</div>
            </div>
          </SettingsCard>
          <SettingsCard style={{ padding: 16, display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", color: "#16a34a", fontSize: 20 }}>
              <i className="fas fa-check-circle" />
            </div>
            <div>
              <div style={{ fontSize: 20, fontWeight: 800, color: "#16a34a" }}>{configuredCount}</div>
              <div style={{ fontSize: 11, color: "#64748b" }}>Configured</div>
            </div>
          </SettingsCard>
          <SettingsCard style={{ padding: 16, display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: "#fffbeb", display: "flex", alignItems: "center", justifyContent: "center", color: "#d97706", fontSize: 20 }}>
              <i className="fas fa-clock" />
            </div>
            <div>
              <div style={{ fontSize: 20, fontWeight: 800, color: "#d97706" }}>{locations.length - configuredCount}</div>
              <div style={{ fontSize: 11, color: "#64748b" }}>Pending Setup</div>
            </div>
          </SettingsCard>
        </div>

        {/* Locations DataTable — row click navigates in */}
        <SettingsCard>
          {/* <SettingsSectionHeader>
            <i className="fas fa-list" style={{ fontSize: 12, color: "#3f88a5" }} />
            <SettingsSectionTitle>Location Access</SettingsSectionTitle>
            <SettingsCountChip>{filtered.length}</SettingsCountChip>
          </SettingsSectionHeader> */}
          <DataTable
            title=""
            columns={columns}
            data={locationRows}
            searchable={true}
            pageSize={15}
            showPagination={true}
            emptyMessage="No locations found."
            onView={row => onLocationClick(locations.find(l => l.id === row.id) || row)}
          />
        </SettingsCard>
      </SettingsContent>
    </>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function OwnershipControl() {
  const [canCreate] = usePermission("ownership-control.create", "OWNERSHIP_CONTROL");
  const [canUpdate] = usePermission("ownership-control.update", "OWNERSHIP_CONTROL");
  const [canDelete] = usePermission("ownership-control.delete", "OWNERSHIP_CONTROL");

  const [selectedLoc, setSelectedLoc] = useState(null);
  const [locations, setLocations] = useState([]);
  const [locLoading, setLocLoading] = useState(true);

  const fyData = useMemo(() => {
    try { const r = localStorage.getItem("financialYears") || localStorage.getItem("financialYearData"); return r ? JSON.parse(r) : []; } catch { return []; }
  }, []);

  const [financialYears, setFYs] = useState(fyData);
  const [fyLoading, setFyLoad] = useState(fyData.length === 0);
  const latestFYId = useMemo(() => getLatestFY(financialYears)?.id, [financialYears]);

  const [selectedFY, setSelectedFY] = useState(() => {
    if (!fyData.length) return null;
    const lid = getLatestFY(fyData)?.id;
    return fyData.find(f => f.id === lid) ?? fyData[0];
  });

  useEffect(() => {
    if (financialYears.length > 0 && !selectedFY) {
      const lid = getLatestFY(financialYears);
      setSelectedFY(financialYears.find(f => f.id === lid) ?? financialYears[0]);
    }
  }, [financialYears, selectedFY]);

  const [configs, setConfigs] = useState({});
  const [allConfigs, setAllConfigs] = useState({});
  const [cfgLoading, setCfgLoading] = useState({});
  const [toast, setToast] = useState(null);

  useEffect(() => {
    (async () => {
      const { isSuccess, data } = await apiCall(`${config.POSTLOGIN_API_URL_COMPANY}getSource`, {}, {}, "GET");
      if (isSuccess && data?.data) {
        setLocations(data.data.reverse().map(item => ({
          id: item.id,
          unitCode: item.unitCode || null,
          location: [item?.location?.area, item?.location?.city, item?.location?.state, item?.location?.country, item?.location?.zipCode].filter(Boolean).join(", "),
          _area: item?.location?.area || "",
          _city: item?.location?.city || "",
          _zipCode: item?.location?.zipCode || "",
          subLocation: item?.subLocation || [],
        })));
      }
      setLocLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (financialYears.length > 0) { setFyLoad(false); return; }
    (async () => {
      const cached = localStorage.getItem("financialYearData");
      if (cached) {
        try {
          const p = JSON.parse(cached);
          if (Array.isArray(p) && p.length) {
            setFYs(p); const lid = getLatestFY(p); setSelectedFY(p.find(f => f.id === lid) ?? p[0]); setFyLoad(false); return;
          }
        } catch { }
      }
      const { isSuccess, data } = await apiCall(`${config.POSTLOGIN_API_URL_COMPANY}getFinancialYear`, {}, {}, "GET");
      if (isSuccess && data?.data) {
        localStorage.setItem("financialYearData", JSON.stringify(data.data));
        localStorage.setItem("financialYears", JSON.stringify(data.data));
        setFYs(data.data);
        const lid = getLatestFY(data.data);
        setSelectedFY(data.data.find(f => f.id === lid) ?? data.data[0]);
      }
      setFyLoad(false);
    })();
  }, [financialYears.length]);

  const isViewOnly = selectedFY ? selectedFY.id !== latestFYId : false;

  const fetchBoth = useCallback(async (locIds, fyId) => {
    const loadState = locIds.reduce((a, id) => ({ ...a, [id]: true }), {});
    setCfgLoading(p => ({ ...p, ...loadState }));
    const [enRes, allRes] = await Promise.all([
      apiCall(`${config.POSTLOGIN_API_URL_COMPANY}ownershipControl`, {}, { locationIds: locIds, financialYearId: fyId, isDisabled: false }, "GET"),
      apiCall(`${config.POSTLOGIN_API_URL_COMPANY}ownershipControl`, {}, { locationIds: locIds, financialYearId: fyId }, "GET"),
    ]);
    const byEn = locIds.reduce((a, id) => ({ ...a, [`${id}_${fyId}`]: [] }), {});
    const byAll = locIds.reduce((a, id) => ({ ...a, [`${id}_${fyId}`]: [] }), {});
    if (enRes.isSuccess && Array.isArray(enRes.data)) enRes.data.forEach(r => { const k = `${r.locationId}_${fyId}`; if (byEn[k]) byEn[k].push(r); });
    if (allRes.isSuccess && Array.isArray(allRes.data)) allRes.data.forEach(r => { const k = `${r.locationId}_${fyId}`; if (byAll[k]) byAll[k].push(r); });
    setConfigs(p => ({ ...p, ...byEn }));
    setAllConfigs(p => ({ ...p, ...byAll }));
    const doneState = locIds.reduce((a, id) => ({ ...a, [id]: false }), {});
    setCfgLoading(p => ({ ...p, ...doneState }));
  }, []);

  useEffect(() => {
    if (!selectedFY || !locations.length) return;
    const fyId = selectedFY.id;
    if (locations.some(l => configs[`${l.id}_${fyId}`] !== undefined)) return;
    fetchBoth(locations.map(l => l.id), fyId);
  }, [selectedFY, locations, fetchBoth]);

  useEffect(() => {
    if (!selectedLoc || !selectedFY) return;
    const key = `${selectedLoc.id}_${selectedFY.id}`;
    if (configs[key] !== undefined) return;
    fetchBoth([selectedLoc.id], selectedFY.id);
  }, [selectedLoc, selectedFY, fetchBoth]);

  const refresh = useCallback(async locId => {
    await fetchBoth([locId], selectedFY.id);
  }, [fetchBoth, selectedFY]);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3200);
  };

  const handleSave = useCallback(async (locId, fd, mode, existing) => {
    try {
      if (mode === "correction" && existing?.id) {
        const { isSuccess } = await apiCall(`${config.POSTLOGIN_API_URL_COMPANY}ownershipControl/${existing.id}`, {}, fd, "PATCH");
        if (!isSuccess) throw new Error("Update failed");
      } else {
        const { isSuccess } = await apiCall(`${config.POSTLOGIN_API_URL_COMPANY}ownershipControl`, {}, fd, "POST");
        if (!isSuccess) throw new Error("Create failed");
      }
      await refresh(locId);
      showToast(mode === "correction" ? "Configuration updated!" : "Configuration added!");
    } catch (err) { showToast(err.message || "Something went wrong", "error"); }
  }, [refresh]);

  const handleDelete = useCallback(async (locId, cfgId) => {
    try {
      const { isSuccess } = await apiCall(`${config.POSTLOGIN_API_URL_COMPANY}ownershipControl/${cfgId}/disable`, {}, {}, "PATCH");
      if (!isSuccess) throw new Error("Delete failed");
      await refresh(locId);
      showToast("Configuration deleted.");
    } catch (err) { showToast(err.message || "Something went wrong", "error"); }
  }, [refresh]);

  const getC = id => selectedFY ? configs[`${id}_${selectedFY.id}`] : undefined;
  const getAC = id => selectedFY ? allConfigs[`${id}_${selectedFY.id}`] : undefined;

  const configuredCount = useMemo(
    () => locations.filter(l => { const c = getC(l.id); return Array.isArray(c) && c.length > 0; }).length,
    [locations, configs, selectedFY]
  );

  const handleLocClick = loc => {
    setSelectedLoc(loc);
    if (selectedFY && configs[`${loc.id}_${selectedFY.id}`] === undefined) fetchBoth([loc.id], selectedFY.id);
  };
  const handleFYChange = fy => { setSelectedFY(fy); setSelectedLoc(null); };

  return (
    <SettingsPage>
      <style>{`
        @keyframes obs-spin        { to { transform:rotate(360deg) } }
        @keyframes obs-toast-in    { from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)} }
        @keyframes obs-dropdown-in { from{opacity:0;transform:translateY(-5px)}to{opacity:1;transform:translateY(0)} }
        * { box-sizing:border-box; }
      `}</style>

      {toast && (
        <div style={{ position: "fixed", bottom: 22, right: 22, background: toast.type === "error" ? "#dc2626" : "#16a34a", color: "#fff", padding: "10px 16px", borderRadius: 9, zIndex: 9999, fontSize: 12, fontWeight: 600, boxShadow: "0 4px 20px rgba(0,0,0,0.15)", animation: "obs-toast-in 0.25s ease", display: "flex", alignItems: "center", gap: 7 }}>
          <span>{toast.type === "error" ? "✕" : "✓"}</span> {toast.msg}
        </div>
      )}

      {selectedLoc ? (
        <LocationDetail
          loc={selectedLoc}
          allConfigs={getAC(selectedLoc.id) || []}
          configLoading={!!cfgLoading[selectedLoc.id]}
          isViewOnly={isViewOnly}
          onSave={handleSave}
          onDelete={handleDelete}
          onBack={() => setSelectedLoc(null)}
          selectedFY={selectedFY}
          financialYears={financialYears}
          latestFYId={latestFYId}
          onFYChange={handleFYChange}
          canCreate={canCreate}
          canUpdate={canUpdate}
          canDelete={canDelete}
        />
      ) : (
        <LocationsList
          locations={locations}
          locLoading={locLoading}
          configs={configs}
          isViewOnly={isViewOnly}
          financialYears={financialYears}
          selectedFY={selectedFY}
          fyLoading={fyLoading}
          latestFYId={latestFYId}
          onFYChange={handleFYChange}
          onLocationClick={handleLocClick}
          configuredCount={configuredCount}
        />
      )}
    </SettingsPage>
  );
}